import { stringToNormalizedRgbColor } from "libs/colors";
import compact from "lodash-es/compact";
import type { TracingType } from "types/api_types";
import { TracingTypeEnum } from "types/api_types";
import type { Vector3 } from "viewer/constants";
import type { SaveQueueType } from "viewer/model/actions/save_actions";
import type {
  EditableMapping,
  IdReservation,
  MipLayerConfig,
  ReadOnlyTracing,
  SkeletonTracing,
  StoreAnnotation,
  UserBoundingBox,
  VolumeTracing,
  WebknossosState,
} from "viewer/store";
import BoundingBox from "../bucket_data_handling/bounding_box";
import { reuseInstanceOnEquality } from "./accessor_helpers";
import { getDataLayers, getLayerBoundingBox, getLayerBoundingBoxId } from "./dataset_accessor";

export function maybeGetSomeTracing(
  annotation: StoreAnnotation,
): SkeletonTracing | VolumeTracing | ReadOnlyTracing | null {
  if (annotation.skeleton != null) {
    return annotation.skeleton;
  } else if (annotation.volumes.length > 0) {
    return annotation.volumes[0];
  } else if (annotation.readOnly != null) {
    return annotation.readOnly;
  }

  return null;
}

export function getSomeTracing(
  annotation: StoreAnnotation,
): SkeletonTracing | VolumeTracing | ReadOnlyTracing {
  const maybeSomeTracing = maybeGetSomeTracing(annotation);

  if (maybeSomeTracing == null) {
    throw new Error("The active annotation does not contain skeletons nor volume data");
  }

  return maybeSomeTracing;
}

export function hasTracing(annotation: StoreAnnotation): boolean {
  // This function ignores ReadOnlyTracings. These only exist when essentially
  // no annotation and no tracing exists. It does NOT mean that the active
  // annotation is editable (could still be read-only due to permissions).
  // This is an implementation of the null-pattern, but should be refactored away.
  // Also see https://github.com/scalableminds/webknossos/issues/9609.
  const maybeTracing = maybeGetSomeTracing(annotation);
  return maybeTracing != null && maybeTracing.type !== "readonly";
}

export function getTracingType(annotation: StoreAnnotation): TracingType {
  if (annotation.skeleton != null && annotation.volumes.length > 0) {
    return TracingTypeEnum.hybrid;
  } else if (annotation.skeleton != null) {
    return TracingTypeEnum.skeleton;
  } else if (annotation.volumes.length > 0) {
    return TracingTypeEnum.volume;
  }

  throw new Error("The active annotation does not contain skeletons nor volume data");
}
export function selectTracing(
  state: WebknossosState,
  tracingType: SaveQueueType,
  tracingId: string,
): SkeletonTracing | VolumeTracing | EditableMapping {
  let tracing;

  switch (tracingType) {
    case "skeleton": {
      tracing = state.annotation.skeleton;
      break;
    }
    case "volume": {
      tracing = state.annotation.volumes.find(
        (volumeTracing) => volumeTracing.tracingId === tracingId,
      );
      break;
    }
    case "mapping": {
      tracing = state.annotation.mappings.find((mapping) => mapping.tracingId === tracingId);
      break;
    }
    default: {
      throw new Error(`Unknown tracing type: ${tracingType}`);
    }
  }

  if (tracing == null) {
    throw new Error(`${tracingType} object with id ${tracingId} not found`);
  }

  return tracing;
}

export function getTracingById(
  state: WebknossosState,
  tracingId: string,
): SkeletonTracing | VolumeTracing | EditableMapping {
  const allTracings = compact([
    state.annotation.skeleton,
    ...state.annotation.volumes,
    ...state.annotation.mappings,
  ]);
  const tracing = allTracings.find((t) => t.tracingId === tracingId);

  if (tracing == null) {
    throw new Error(`Could not find tracing with id ${tracingId}.`);
  }

  return tracing;
}

function _getTaskBoundingBoxes(state: WebknossosState) {
  const { annotation, task } = state;
  if (task == null) return {};
  const layers = compact([annotation.skeleton, ...annotation.volumes]);
  return Object.fromEntries(layers.map((l) => [l.tracingId, l.boundingBox]));
}

export const getTaskBoundingBoxes = reuseInstanceOnEquality(_getTaskBoundingBoxes);

export const getUserBoundingBoxesFromState = (state: WebknossosState): UserBoundingBox[] => {
  const maybeSomeTracing = maybeGetSomeTracing(state.annotation);
  return maybeSomeTracing != null ? maybeSomeTracing.userBoundingBoxes : [];
};

export const getUserBoundingBoxesThatContainPosition = (
  state: WebknossosState,
  position: Vector3,
): UserBoundingBox[] => {
  const bboxes = getUserBoundingBoxesFromState(state);

  return bboxes.filter((el) => new BoundingBox(el.boundingBox).containsPoint(position));
};

export function getIdReservationsForBoundingBoxes(state: WebknossosState): IdReservation[] {
  return state.localAnnotationState.idReservationsForBoundingBoxes;
}

export type MipEnabledBBox = { bbox: UserBoundingBox; configs: MipLayerConfig[] };

// Layer bounding boxes are read-only and not part of the annotation's user bounding boxes, so they
// are represented here as synthetic UserBoundingBox-shaped entries (stable negative id, see
// getLayerBoundingBoxId) purely to reuse the existing MIP wiring (mipBBoxSettings, scene_controller).
function getLayerBoundingBoxesAsUserBoundingBoxes(state: WebknossosState): UserBoundingBox[] {
  return getDataLayers(state.dataset).map((layer, index) => ({
    id: getLayerBoundingBoxId(index),
    name: layer.name,
    boundingBox: getLayerBoundingBox(state.dataset, layer.name),
    color:
      state.temporaryConfiguration.layerBoundingBoxColors[layer.name] ??
      stringToNormalizedRgbColor(layer.name),
    isVisible: state.temporaryConfiguration.layerBoundingBoxVisibilities[layer.name] ?? false,
  }));
}

export const getMipEnabledBBoxes = reuseInstanceOnEquality(
  (state: WebknossosState): MipEnabledBBox[] => {
    const { mipBBoxSettings } = state.uiInformation;
    if (Object.keys(mipBBoxSettings).length === 0) {
      return [];
    }
    const bboxes = [
      ...getUserBoundingBoxesFromState(state),
      ...getLayerBoundingBoxesAsUserBoundingBoxes(state),
    ];
    return bboxes.flatMap((bbox) => {
      const configs = mipBBoxSettings[bbox.id];
      return configs != null && configs.length > 0 ? [{ bbox, configs }] : [];
    });
  },
);
